/**
 * Centralized HTTP Client
 * SECURITY: All API calls MUST go through the API Gateway
 *
 * Features:
 * - Automatic JWT authentication
 * - Account isolation headers
 * - Request/response interceptors
 * - Error handling
 * - Timeout management
 *
 * TASK-083: Enforces gateway-only communication for RLS and account isolation
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { API_CONFIG, API_TIMEOUTS } from '../config/api-config';
import { purgeDockScopeStorage } from '../stores/createChatStore';

/**
 * HTTP Client Configuration
 */
interface HttpClientConfig {
  baseURL?: string;
  timeout?: number;
  enableAuth?: boolean;
  enableRetry?: boolean;
}

/**
 * Pull the human-readable reason out of a FastAPI error body.
 *
 * FastAPI answers with `{detail: "..."}` for raised HTTPExceptions and with
 * `{detail: [{loc, msg, ...}]}` for request-validation failures, so both shapes have to be
 * understood. Anything else returns null and the caller keeps Axios' own message.
 */
export function extractErrorDetail(data: unknown): string | null {
  if (typeof data === 'string' && data.trim()) return data;
  if (!data || typeof data !== 'object') return null;

  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    // Validation errors: "body.email: value is not a valid email address"
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (!item || typeof item !== 'object') return null;
        const { loc, msg } = item as { loc?: unknown[]; msg?: string };
        if (!msg) return null;
        const field = Array.isArray(loc) ? loc.join('.') : null;
        return field ? `${field}: ${msg}` : msg;
      })
      .filter((msg): msg is string => Boolean(msg));
    if (messages.length) return messages.join('; ');
  }

  return null;
}

/**
 * Centralized HTTP Client Class
 * Single source for all backend communication
 */
class HttpClient {
  private client: AxiosInstance;
  private readonly maxRetries = 3;
  private readonly retryDelay = 1000; // ms

  constructor(config?: HttpClientConfig) {
    // SECURITY: Force all requests through gateway
    const baseURL = config?.baseURL || API_CONFIG.GATEWAY_URL;

    // Verify we're using gateway URL
    if (!baseURL.includes(':8000') && !baseURL.includes('gateway')) {
      console.warn('⚠️ HTTP Client should use GATEWAY_URL. Received:', baseURL);
    }

    this.client = axios.create({
      baseURL,
      timeout: config?.timeout || API_TIMEOUTS.DEFAULT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptors
    this.setupRequestInterceptor(config?.enableAuth !== false);
    this.setupResponseInterceptor(config?.enableRetry !== false);
  }

  /**
   * Request Interceptor
   * Adds authentication headers to all requests
   */
  private setupRequestInterceptor(enableAuth: boolean) {
    this.client.interceptors.request.use(
      (config) => {
        if (enableAuth) {
          // Get JWT token from localStorage
          const token = localStorage.getItem('auth_token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }

          // Add account ID header for RLS (row-level security)
          const accountId = localStorage.getItem('account_id');
          if (accountId) {
            config.headers['X-Account-ID'] = accountId;
          }

          // Log for debugging (remove in production)
          if (import.meta.env.MODE === 'development') {
            console.log(`[HTTP] ${config.method?.toUpperCase()} ${config.url}`, {
              hasAuth: !!token,
              hasAccountId: !!accountId,
              baseURL: config.baseURL
            });
          }
        }

        return config;
      },
      (error) => {
        console.error('[HTTP] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Response Interceptor
   * Handles errors and retries
   */
  private setupResponseInterceptor(enableRetry: boolean) {
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Success response
        if (import.meta.env.MODE === 'development') {
          console.log(`[HTTP] ✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            dataSize: JSON.stringify(response.data).length
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: number };

        // Handle authentication errors
        if (error.response?.status === 401) {
          console.warn('[HTTP] 401 Unauthorized - Clearing auth and redirecting');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('account_id');
          purgeDockScopeStorage(); // TASK-710: session ended — drop scoped threads

          // Redirect to demo login (avoid infinite loop)
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        // Handle account isolation errors
        if (error.response?.status === 403) {
          console.error('[HTTP] 403 Forbidden - Account isolation violation');
          return Promise.reject(new Error('Access denied: Account isolation error'));
        }

        // Retry logic for network errors
        if (enableRetry && this.shouldRetry(error) && (!originalRequest._retry || originalRequest._retry < this.maxRetries)) {
          originalRequest._retry = (originalRequest._retry || 0) + 1;

          console.warn(`[HTTP] Retrying request (${originalRequest._retry}/${this.maxRetries})...`);

          // Exponential backoff
          await this.delay(this.retryDelay * originalRequest._retry);

          return this.client(originalRequest);
        }

        // Log error details
        console.error('[HTTP] Request failed:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });

        // Axios sets `message` to "Request failed with status code 500" — true and useless.
        // FastAPI puts the reason in `detail`; lift it onto the error so every caller that
        // renders `err.message` shows what actually went wrong. The AxiosError itself is
        // still what gets rejected, so callers inspecting `error.response.status` are
        // unaffected.
        const detail = extractErrorDetail(error.response?.data);
        if (detail) {
          error.message = detail;
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Determine if request should be retried
   */
  private shouldRetry(error: AxiosError): boolean {
    // Retry on network errors or 5xx errors
    return (
      !error.response || // Network error
      (error.response.status >= 500 && error.response.status < 600) // Server error
    );
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Get raw axios instance (for special cases)
   * @deprecated Use typed methods instead
   */
  getRawClient(): AxiosInstance {
    console.warn('⚠️ Using raw axios client. Prefer typed methods (get, post, etc.)');
    return this.client;
  }

  /**
   * Update base URL (for testing)
   */
  setBaseURL(baseURL: string) {
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Update authentication token
   */
  setAuthToken(token: string) {
    localStorage.setItem('auth_token', token);
  }

  /**
   * Clear authentication token
   */
  clearAuthToken() {
    localStorage.removeItem('auth_token');
  }
}

/**
 * Default HTTP client instance
 * SECURITY: Configured to use gateway only
 */
export const httpClient = new HttpClient({
  baseURL: API_CONFIG.GATEWAY_URL,
  timeout: API_TIMEOUTS.DEFAULT,
  enableAuth: true,
  enableRetry: true,
});

/**
 * HTTP client for long-running operations
 */
export const httpClientLongRunning = new HttpClient({
  baseURL: API_CONFIG.GATEWAY_URL,
  timeout: API_TIMEOUTS.LONG_RUNNING,
  enableAuth: true,
  enableRetry: true,
});

/**
 * HTTP client for uploads
 */
export const httpClientUpload = new HttpClient({
  baseURL: API_CONFIG.GATEWAY_URL,
  timeout: API_TIMEOUTS.UPLOAD,
  enableAuth: true,
  enableRetry: false, // Don't retry uploads
});

// Export class for custom instances
export { HttpClient };
export type { HttpClientConfig };
